import React, { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../utils/axiosInstance";
import NotificationContext from "../../components/notification/NotificationContext";
import { useNavigate } from "react-router-dom";
import socketUrl from "../../utils/socketUrl";
import { createSocket } from "../../utils/ws";

// Read the current token each time (not once at module load).
const getToken = () => localStorage.getItem("__admin__token&") || sessionStorage.getItem("__admin__token&") || null;

const DashboardContext = (props) => {
    const history = useNavigate();

    const [state, setState] = useState({
        id: null,
        first_name: "",
        last_name: "",
        email: "",
        profile_pic: null,
        phone_number: "",
        address_line_1: "",
        address_line_2: "",
        country: "",
        country_code: "",
        city: "",
        state: "",
        zip_code: "",
        timezone: "",
    });

    const [permis, setPermis] = useState({});
    const [contextData, setContextData] = useState();
    const [emailData, setEmailData] = useState();

    const [activeNotification, setActiveNotification] = useState();
    const [activeCount, setActiveCount] = useState(0);
    const [adminAccess, setAdminAccess] = useState();
    const [accessCount, setAccessCount] = useState(0);

    const [ID, setID] = useState(-1);
    const [realtimeUnavailable, setRealtimeUnavailable] = useState(false);

    // Fetch profile data
    const fetchProfile = useCallback(async () => {
        if (getToken() != null) {
            try {
                const response = await axiosInstance.get("admin_dash/edit_admin_profile");
                setState(response.data.response.user);
                setID(response.data.response.user.id);
            } catch (err) {
                // Only an auth failure logs out; a network error / backend outage must not.
                const status = err?.response?.status;
                if (status === 401 || status === 403) {
                    sessionStorage.removeItem("__admin__token&");
                    localStorage.removeItem("__admin__token&");
                    history("/", { replace: true });
                    window.location.reload();
                }
                console.error(err);
            }
        }
    }, [history]);

    // Fetch permissions
    const fetchPermissions = useCallback(async () => {
        if (getToken() != null) {
            try {
                const response = await axiosInstance.get("/permissions");
                setPermis(response.data.response);
            } catch (err) {
                console.error(err.response);
            }
        }
    }, []);

    // Fetch email permissions
    const fetchEmailPermissions = useCallback(async () => {
        if (getToken() != null) {
            try {
                const response = await axiosInstance.get("admin_dash/email_permission");
                setEmailData(response.data.response.data);
            } catch (err) {
                console.error(err.response.data);
            }
        }
    }, []);

    // Realtime feeds. Each socket is opened in an effect that returns its cleanup (the old async fetch functions
    // returned the cleanup to nobody, so sockets leaked). The server sends nothing until the first message
    // {token}, which is sent in onOpen and therefore also on every reconnect. Nothing connects until the admin id
    // (and so a valid session) is known.
    const feedRefs = useRef({});

    const openFeed = useCallback((name, path, onData) => {
        const handle = createSocket({
            getUrl: () => (getToken() ? socketUrl(path) : null),
            onOpen: (h) => {
                h.send({ token: getToken() });
            },
            onMessage: (data) => onData(data),
            onClose: (event) => {
                // 4001/4002/4003 = the server refused our token; retrying cannot help.
                if (event && event.code >= 4001 && event.code <= 4003) {
                    console.warn(`Realtime feed ${name} refused by server (code ${event.code}).`);
                    handle.close();
                    setRealtimeUnavailable(true);
                }
            },
            onGiveUp: () => {
                console.warn(`Realtime feed ${name} unavailable; giving up reconnecting.`);
                setRealtimeUnavailable(true);
            },
        });
        feedRefs.current[name] = handle;
        return () => {
            handle.close();
            if (feedRefs.current[name] === handle) delete feedRefs.current[name];
        };
    }, []);

    // Mark one item read on a feed. Returns false (and does nothing else) if that socket is not open.
    const markRead = useCallback((name, id) => {
        const handle = feedRefs.current[name];
        return handle ? handle.send({ token: getToken(), text: id }) : false;
    }, []);
    const markNotificationRead = useCallback((id) => markRead("notifications", id), [markRead]);
    const markAccessRead = useCallback((id) => markRead("access", id), [markRead]);
    const markActivityRead = useCallback((id) => markRead("activities", id), [markRead]);

    useEffect(() => {
        if (ID === -1 || ID == null) return undefined;
        return openFeed("activities", "ws/admin_activites/", (data) => {
            if (data.type === "notification") {
                setActiveCount(data.notifications_unread);
                setActiveNotification(data.notifications);
            }
        });
    }, [ID, openFeed]);

    useEffect(() => {
        if (ID === -1 || ID == null) return undefined;
        return openFeed("access", "ws/admin_access/", (data) => {
            // initial payload is type "notificationd" (sic), later pushes are "notification"
            if (data.type === "notification" || data.type === "notificationd") {
                setAccessCount(data.notifications_unread);
                setAdminAccess(data.notifications);
            }
        });
    }, [ID, openFeed]);

    useEffect(() => {
        if (ID === -1 || ID == null) return undefined;
        return openFeed("notifications", `ws/admin_notifications/${ID}/`, (data) => {
            if (data.type === "notification") setContextData(data);
        });
    }, [ID, openFeed]);

    // Separate useEffect hooks for each function

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    useEffect(() => {
        fetchEmailPermissions();
    }, [fetchEmailPermissions]);

    return (
        <NotificationContext.Provider
            value={{
                data: contextData,
                profile: state,
                permis,
                activeCount,
                activeNotification,
                adminAccess,
                accessCount,
                emailData,
                // Key names the pages actually read (AdminActivities, AdminAccess, EmailNotification, admindashbord)
                activenotification: activeNotification,
                activecount: activeCount,
                adminaccessnotify: adminAccess,
                adminaccesscount: accessCount,
                email_data: emailData,
                notificationsUnread: contextData ? contextData.notifications_unread : 0,
                realtimeUnavailable,
                markNotificationRead,
                markAccessRead,
                markActivityRead,
            }}
        >
            {props.children}
        </NotificationContext.Provider>
    );
};

export default DashboardContext;
